import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { authUserMetaAtom } from '@/store/authAtoms';
import {
  ICODE_CAPABILITIES,
  DEFAULT_ICODE_CAPS,
  GFE_REPORTED_CAPABILITIES,
  GFE_UNREPORTED_CAPABILITIES,
  type ICodeCapabilities,
} from '@/features/auth/icodeCapabilities';

const GUEST_ALLOWED_ROUTES = new Set(['/appointments', '/help']);

export interface SessionCaps {
  /** true when the current session was created via an iCode verify (restricted). */
  isICodeSession: boolean;
  /** true when the current session was created via the guest schedule flow (restricted). */
  isGuestSession: boolean;
  /** Resolved capabilities for the active iCode type, or null for full sessions. */
  caps: ICodeCapabilities | null;
  /** Whether a given pathname is reachable under the current session. */
  canAccess: (pathname: string) => boolean;
}

/**
 * Reads the active session metadata from authUserMetaAtom and resolves it
 * against the iCode capability map. Full (username/password) BFF sessions
 * are unrestricted and canAccess always returns true.
 */
export function useSessionCaps(): SessionCaps {
  const user = useAtomValue(authUserMetaAtom);

  return useMemo<SessionCaps>(() => {
    const session = user?.session;

    if (session?.type === 'guest') {
      return {
        isICodeSession: false,
        isGuestSession: true,
        caps: null,
        canAccess: (pathname: string) => {
          const firstSegment = `/${pathname.split('/').filter(Boolean)[0] ?? ''}`;
          return GUEST_ALLOWED_ROUTES.has(firstSegment);
        },
      };
    }

    if (session?.type === 'icode') {
      const caps = session.iCodeType === 'GFE'
        ? (session.isExamReported ? GFE_REPORTED_CAPABILITIES : GFE_UNREPORTED_CAPABILITIES)
        : ICODE_CAPABILITIES[session.iCodeType] ?? DEFAULT_ICODE_CAPS;
      const allowed = caps.allowedRoutes as ReadonlySet<string>;
      return {
        isICodeSession: true,
        isGuestSession: false,
        caps,
        canAccess: (pathname: string) => {
          const firstSegment = `/${pathname.split('/').filter(Boolean)[0] ?? ''}`;
          return allowed.has(firstSegment);
        },
      };
    }

    return { isICodeSession: false, isGuestSession: false, caps: null, canAccess: () => true };
  }, [user]);
}
